import { onMounted, onBeforeUnmount, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'

/**
 * 내비게이션 배지 갱신.
 *
 * 배지는 셸이 책임진다. 예전에는 Navigation.vue 의 onMounted 에서만 조회했고
 * Navigation 은 언마운트되지 않아 **세션 내내 숫자가 얼어붙어** 있었다.
 * 셸을 다시 만들면서 그 호출부가 사라져 캘린더에서 새로고침하면 배지가 아예
 * 뜨지 않는 회귀가 생겼는데, 화면마다 dispatch 를 흩뿌리는 대신 여기 하나로 모은다.
 *
 * 갱신 시점 네 가지:
 *   1. 마운트 (= 새로고침/최초 진입)
 *   2. 탭 이동 (스로틀)
 *   3. 탭 복귀(visibilitychange) — 모바일에서 앱이 몇 시간씩 백그라운드에 있다
 *   4. 보이는 동안 주기 폴링
 *
 * 알림 카운트도 여기서 함께 갱신한다. 목록은 시트를 열 때만 가져온다 —
 * 폴링이 목록까지 끌어오면 낭비다.
 */
const THROTTLE_MS = 30 * 1000
const POLL_MS = 60 * 1000

export function useBadgeRefresh() {
  const store = useStore()
  const route = useRoute()

  let lastFetch = 0
  let timer = null

  function refresh({ force = false } = {}) {
    if (!store.getters.isAuthenticated) return
    const now = Date.now()
    if (!force && now - lastFetch < THROTTLE_MS) return
    lastFetch = now

    store.dispatch('fetchTaskBadges').catch(() => {})
    store.dispatch('fetchNotificationCount').catch(() => {})
    if (store.getters.canReport) {
      // 보고서 결재 + 업무 완료요청을 합친 숫자. 보고 탭 배지가 이걸 쓴다.
      store.dispatch('fetchApprovalCount').catch(() => {})
    }
  }

  function onVisibility() {
    if (document.visibilityState === 'visible') {
      refresh()
      startPolling()
    } else {
      stopPolling()
    }
  }

  function startPolling() {
    if (timer) return
    timer = setInterval(() => refresh({ force: true }), POLL_MS)
  }

  function stopPolling() {
    if (timer) {
      clearInterval(timer)
      timer = null
    }
  }

  onMounted(() => {
    refresh({ force: true })
    document.addEventListener('visibilitychange', onVisibility)
    if (document.visibilityState === 'visible') startPolling()
  })

  onBeforeUnmount(() => {
    document.removeEventListener('visibilitychange', onVisibility)
    stopPolling()
  })

  // 탭 이동 시 (스로틀이 걸려 있어 연타해도 요청이 몰리지 않는다)
  watch(() => route.path, () => refresh())

  return { refresh }
}
